import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import { trackEvent } from "@/lib/analytics";
import { SubpageShell } from "@/components/landing/SubpageShell";
import { CheckoutModal } from "@/components/landing/CheckoutModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Block = ({ text }) => {
    if (text.startsWith("### "))
        return (
            <h3 className="mt-8 font-display text-lg font-semibold text-white">
                {text.slice(4)}
            </h3>
        );
    if (text.startsWith("## "))
        return (
            <h2 className="mt-10 font-display text-2xl font-bold tracking-tight text-white">
                {text.slice(3)}
            </h2>
        );
    if (text.startsWith("- "))
        return (
            <ul className="mt-4 flex flex-col gap-2.5">
                {text.split("\n").map((li, i) => (
                    <li key={i} className="flex items-start gap-3 text-base leading-relaxed text-slate-300">
                        <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-sky-400" />
                        {li.replace(/^- /, "")}
                    </li>
                ))}
            </ul>
        );
    return (
        <p className="mt-5 text-base leading-relaxed text-slate-300">{text}</p>
    );
};

export default function BlogPost() {
    const { slug } = useParams();
    const [post, setPost] = useState(null);
    const [missing, setMissing] = useState(false);
    const [checkoutOpen, setCheckoutOpen] = useState(false);

    useEffect(() => {
        trackEvent("page_view");
        setPost(null);
        setMissing(false);
        fetch(`${API}/blog/${slug}`)
            .then((r) => {
                if (!r.ok) throw new Error();
                return r.json();
            })
            .then(setPost)
            .catch(() => setMissing(true));
    }, [slug]);

    const openQuote = () => {
        trackEvent("quote_open");
        setCheckoutOpen(true);
    };

    return (
        <SubpageShell onQuote={openQuote}>
            {missing ? (
                <div data-testid="blog-post-missing" className="py-20 text-center">
                    <h1 className="font-display text-3xl font-bold text-white">
                        Article not found
                    </h1>
                    <a
                        href="/blog"
                        className="mt-4 inline-flex items-center gap-2 text-sky-400 hover:text-sky-300"
                    >
                        <ArrowLeft className="h-4 w-4" /> Back to the blog
                    </a>
                </div>
            ) : !post ? (
                <p className="py-20 text-center text-sm text-slate-500">Loading…</p>
            ) : (
                <>
                    <Seo
                        title={`${post.title} | AdvantaShield`}
                        siteName="AdvantaShield"
                        description={post.excerpt || post.title}
                        image="/og-image.png"
                        jsonLd={{
                            "@context": "https://schema.org",
                            "@type": "Article",
                            headline: post.title,
                            description: post.excerpt,
                            datePublished: post.created_at,
                            dateModified: post.updated_at || post.created_at,
                            author: { "@type": "Organization", name: "AdvantaShield" },
                            publisher: { "@type": "Organization", name: "AdvantaShield" },
                        }}
                    />
                    <a
                        href="/blog"
                        className="inline-flex items-center gap-2 text-sm text-slate-400 transition-colors hover:text-sky-300"
                    >
                        <ArrowLeft className="h-4 w-4" /> All articles
                    </a>
                    <h1 className="mt-6 max-w-3xl font-display text-3xl font-extrabold leading-[1.1] tracking-tight text-white sm:text-4xl">
                        {post.title}
                    </h1>
                    <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">
                        {new Date(post.created_at).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                        })}{" "}
                        · AdvantaShield
                    </p>
                    <article
                        data-testid="blog-post-body"
                        className="mt-4 max-w-3xl pb-4"
                    >
                        {post.body.split(/\n\n+/).map((block, i) => (
                            <Block key={i} text={block.trim()} />
                        ))}
                    </article>
                    <div className="mt-14 rounded-3xl border border-sky-400/20 bg-[#0C1019] p-8 md:p-10">
                        <h2 className="font-display text-2xl font-bold tracking-tight text-white">
                            See what coverage costs{" "}
                            <span className="text-sky-400">for your vehicle.</span>
                        </h2>
                        <p className="mt-3 max-w-xl text-base text-slate-400">
                            A custom quote takes under a minute — monthly, quarterly,
                            or annual payments, and you can cancel anytime.
                        </p>
                        <button
                            data-testid="blog-quote-btn"
                            onClick={openQuote}
                            className="mt-6 inline-flex items-center gap-2.5 rounded-full bg-sky-400 px-7 py-4 text-base font-bold text-[#0A0D14] transition-[background-color,box-shadow,transform] duration-300 hover:bg-sky-300 hover:shadow-[0_0_44px_rgba(56,189,248,0.5)] active:scale-95"
                        >
                            Get My Instant Quote
                            <ArrowRight className="h-5 w-5" />
                        </button>
                    </div>
                </>
            )}
            <CheckoutModal open={checkoutOpen} onOpenChange={setCheckoutOpen} />
        </SubpageShell>
    );
}
